import React, { useState, useEffect } from 'react';
import { Search, User, ShoppingBag, Menu, Globe, ChevronDown, Check } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { MobileMenu } from './MobileMenu';
import { useStoreLogo, HEALTH_STORE_FALLBACK_LOGO } from '../../constants/branding';

interface HeaderProps {
  activeNav: string;
  onNavigate: (navId: string) => void;
  onOpenSearch: () => void;
  onOpenAccount: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  activeNav,
  onNavigate,
  onOpenSearch,
  onOpenAccount,
}) => {
  const { totalItemsCount, openCart } = useCart();
  const storeLogo = useStoreLogo();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'shop-all', label: 'Shop All' },
    { id: 'skincare', label: 'Skincare' },
    { id: 'haircare', label: 'Hair Care' },
    { id: 'supplements', label: 'Supplements' },
    { id: 'bundles', label: 'Bundle Deals' },
    { id: 'blog', label: 'Beauty Blog' },
    { id: 'education-toys', label: 'Education & Toys' },
    { id: 'track-order', label: 'Track Order' },
  ];

  return (
    <header
      id="main-header"
      className={`w-full bg-white transition-all duration-300 z-30 ${
        isScrolled ? 'sticky top-0 shadow-md py-2 sm:py-2.5' : 'relative py-3 sm:py-4 border-b border-slate-100'
      }`}
    >
      <div className="max-w-[1536px] mx-auto px-3 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between gap-3 sm:gap-6">
          
          {/* LEFT: Official Brand Logo (Clickable -> Full reload / Navigate to Home) */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              id="header-logo-btn"
              onClick={() => {
                if (activeNav === 'home' || window.location.pathname === '/') {
                  window.location.href = '/';
                } else {
                  onNavigate('home');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="text-left group flex items-center focus:outline-none transition-all duration-200 hover:opacity-95 active:scale-[0.98] cursor-pointer py-1"
              aria-label="Health Store - Healthy Today, Stronger Tomorrow"
              title="Health Store - Home"
            >
              <img
                src={storeLogo}
                alt="Health Store - Healthy Today, Stronger Tomorrow"
                width="340"
                height="80"
                className="h-12 sm:h-14 md:h-16 lg:h-18 xl:h-20 w-auto max-w-[220px] sm:max-w-[270px] md:max-w-[310px] lg:max-w-[350px] object-contain select-none transition-transform duration-200 group-hover:scale-[1.02] drop-shadow-xs"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (!target.src.includes('health_store_official_logo')) {
                    target.src = HEALTH_STORE_FALLBACK_LOGO;
                  }
                }}
              />
            </button>
          </div>

          {/* CENTER: Desktop Navigation */}
          <nav className="hidden lg:flex items-center justify-center gap-1 xl:gap-2 flex-wrap">
            {navLinks.map((link) => {
              const isActive = activeNav === link.id;
              return (
                <button
                  key={link.id}
                  id={`nav-link-${link.id}`}
                  onClick={() => onNavigate(link.id)}
                  className={`relative px-2.5 py-1.5 text-xs xl:text-[13px] font-bold transition-all rounded-md flex items-center gap-1 ${
                    isActive
                      ? 'text-[#E11D48] underline underline-offset-8 decoration-2 decoration-[#E11D48]'
                      : 'text-[#1E293B] hover:text-[#E11D48]'
                  }`}
                >
                  <span>{link.label}</span>
                </button>
              );
            })}
          </nav>

          {/* RIGHT: Header Actions */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {/* Language / Currency Selector */}
            <div className="relative hidden md:block">
              <button
                id="header-lang-btn"
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-[#191975] py-1.5 px-2 transition-all focus:outline-none"
                aria-label="Select Language and Currency"
              >
                <span>English</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              </button>

              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl py-2 z-50 text-xs">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Store Region
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between text-[#172554] bg-slate-50 font-bold">
                    <span>🇵🇰 Pakistan (PKR - Rs.)</span>
                    <Check className="w-3.5 h-3.5 text-[#191975]" />
                  </div>
                  <div className="px-3 py-1.5 text-[11px] text-slate-500">
                    Language: English
                  </div>
                </div>
              )}
            </div>

            {/* Search Icon */}
            <button
              id="header-search-btn"
              onClick={onOpenSearch}
              className="p-2 sm:p-2.5 rounded-full text-[#172554] hover:text-[#191975] hover:bg-slate-100 transition-colors"
              title="Search products"
              aria-label="Search store"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Account Icon */}
            <button
              id="header-account-btn"
              onClick={onOpenAccount}
              className="p-2 sm:p-2.5 rounded-full text-[#172554] hover:text-[#191975] hover:bg-slate-100 transition-colors"
              title="Account & Orders"
              aria-label="Open customer account"
            >
              <User className="w-5 h-5" />
            </button>

            {/* Shopping Bag / Cart Icon */}
            <button
              id="header-cart-btn"
              onClick={openCart}
              className="relative p-2 sm:p-2.5 rounded-full text-[#172554] hover:text-[#191975] hover:bg-slate-100 transition-colors flex items-center"
              title="Shopping Cart"
              aria-label={`View Cart, ${totalItemsCount} items`}
            >
              <ShoppingBag className="w-5 h-5" />
              {totalItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#191975] text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-pulse">
                  {totalItemsCount}
                </span>
              )}
            </button>

            {/* Mobile Hamburger Button */}
            <button
              id="header-menu-toggle-btn"
              onClick={() => setIsMobileMenuOpen(true)}
              className="xl:hidden p-2 sm:p-2.5 rounded-full text-[#172554] hover:bg-slate-100 transition-colors"
              aria-label="Open mobile navigation"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        activeNav={activeNav}
        onNavigate={onNavigate}
      />
    </header>
  );
};
